/**
 * 드라이버 위치 지정 — `🗄 연결` 창의 '드라이버' 탭.
 *
 * 드라이버가 어디 있는지는 현장에서만 안다(Instant Client 를 어디 풀었는지, 티베로 jar 이 어디 있는지).
 * 그래서 화면에서 지정한다 — LiveSQL 연결 화면의 '드라이버 관리' 탭과 같은 자리다.
 *
 * 우선순위: config\openflow.env > 이 화면 > 꾸러미 동봉(drivers 폴더).
 * 설정 파일이 잡은 값은 이 화면이 덮지 않는다. 대신 칸을 잠그고 그 사실을 적어 준다.
 *
 * 오라클과 티베로는 적용 시점이 다르다.
 *  - 오라클: 클라이언트는 프로세스당 한 번만 불러올 수 있어 **재시작해야** 먹는다.
 *  - 티베로: 접속할 때마다 설정을 읽어 **바로** 먹는다.
 */
import React, { useEffect, useState } from 'react';
import { be1Fetch } from '../../config/api';
import { logUsage } from '../../services/eventLog';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { errorText } from '../../services/errorText';

type Source = 'config' | 'screen' | 'default' | 'bundled' | 'system' | 'detected';
interface Setting { value: string; source: Source }

interface Attempt { path: string; origin: Source; ok: boolean; reason: string }
interface Candidate { path: string; origin: Source; exists: boolean; hasClientLib: boolean; nonAscii: boolean; problem: string }

interface OracleStatus {
  active: {
    requestedMode: string; mode: 'thin' | 'thick'; clientDir: string | null; clientVersion: string | null;
    configDir: string | null; attempts: Attempt[];
  };
  settings: { mode: Setting; clientDir: Setting; configDir: Setting };
  candidates: Candidate[];
  bundledRoot: string;
  restartRequired: boolean;
}

interface TiberoStatus {
  effectiveDriver: 'pyodbc' | 'jdbc';
  settings: { driver: Setting; odbcDriver: Setting; jdbcJar: Setting };
  jdbcJarExists: boolean;
  bundledJars: string[];
  javaFound: boolean;
  bridges: { pyodbc: boolean; jaydebeapi: boolean };
  /** 이 PC 에 설치돼 이 프로그램(64비트)이 쓸 수 있는 티베로 ODBC 드라이버 — 최신 판 먼저 */
  odbcInstalled: string[];
  /** 32비트로만 설치된 것 — 이름이 맞아도 쓸 수 없다 */
  odbcInstalled32Only: string[];
}

interface ProbeResult { loaded: boolean; clientVersion: string | null; error: string; nonAscii: boolean }

/** 설정 출처 표시 — connection:drv.source.<출처> 번역 키. 모르는 출처는 그대로 보여 준다 */
const SOURCES = new Set(['config', 'screen', 'default', 'bundled', 'system', 'detected']);
const sourceLabel = (t: TFunction, src: string) => (SOURCES.has(src) ? t(`connection:drv.source.${src}`) : src);

const inputCls = 'w-full rounded border border-gray-300 px-2 py-1 text-sm mt-0.5 disabled:bg-gray-100 disabled:text-gray-500';
const btnCls = 'text-xs px-2.5 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 whitespace-nowrap';
const primaryCls = 'text-sm px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50';

const lockedNote = (t: TFunction, s?: Setting) =>
  s?.source === 'config'
    ? <div className="text-[0.6875rem] text-amber-700 mt-0.5">{t('connection:drv.locked')}</div>
    : null;

async function json(res: Response) {
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(errorText(body, res.statusText));
  return body;
}

const DriverSettingsPanel: React.FC = () => {
  const { t } = useTranslation();
  const [oracle, setOracle] = useState<OracleStatus | null>(null);
  const [tibero, setTibero] = useState<TiberoStatus | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // 오라클 입력
  const [oMode, setOMode] = useState('auto');
  const [oDir, setODir] = useState('');
  const [oCfg, setOCfg] = useState('');
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [oMsg, setOMsg] = useState('');
  const [oErr, setOErr] = useState(false);          // 메시지가 실패인가 — 문구로 가르면 언어를 바꿀 때 깨진다

  // 티베로 입력
  const [tDriver, setTDriver] = useState('');
  const [tOdbc, setTOdbc] = useState('');
  const [tJar, setTJar] = useState('');
  const [tMsg, setTMsg] = useState('');
  const [tErr, setTErr] = useState(false);

  const load = async () => {
    setError('');
    try {
      const body = await json(await be1Fetch('/api/drivers', { credentials: 'include' }));
      const o: OracleStatus = body.oracle;
      const tb: TiberoStatus = body.tibero;
      setOracle(o);
      setTibero(tb);
      setOMode(o.settings.mode.value || 'auto');
      // 기본값·꾸러미에서 온 값은 칸에 채우지 않는다 — 채우면 저장할 때 '화면에서 지정한 것' 으로 굳는다
      setODir(o.settings.clientDir.source === 'default' ? '' : o.settings.clientDir.value);
      setOCfg(o.settings.configDir.source === 'default' ? '' : o.settings.configDir.value);
      setTDriver(tb.settings.driver.source === 'default' ? '' : tb.settings.driver.value);
      setTOdbc(['default', 'detected'].includes(tb.settings.odbcDriver.source) ? '' : tb.settings.odbcDriver.value);
      setTJar(['default', 'bundled'].includes(tb.settings.jdbcJar.source) ? '' : tb.settings.jdbcJar.value);
    } catch (e: any) {
      setError(e?.message || t('connection:drv.loadFailed'));
    }
  };

  useEffect(() => { load(); }, []);

  const checkOracle = async () => {
    setBusy(true); setProbe(null);
    try {
      const out = await json(await be1Fetch('/api/drivers/oracle/check', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ path: oDir }),
      }));
      setProbe(out);
      logUsage('driver.oracle.check', { target: out.loaded ? 'ok' : 'fail' });
    } catch (e: any) {
      setProbe({ loaded: false, clientVersion: null, error: e?.message || t('connection:drv.checkFailed'), nonAscii: false });
    } finally { setBusy(false); }
  };

  const saveOracle = async () => {
    setBusy(true); setOMsg(''); setOErr(false);
    try {
      const out = await json(await be1Fetch('/api/drivers/oracle', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: oMode, clientDir: oDir, configDir: oCfg }),
      }));
      setOracle(out.oracle);
      setOMsg(out.restartRequired ? t('connection:drv.savedRestart') : t('connection:drv.savedNoRestart'));
      logUsage('driver.oracle.save', { target: oMode });
    } catch (e: any) {
      setOMsg(t('connection:drv.saveFailed', { message: e?.message || '' })); setOErr(true);
    } finally { setBusy(false); }
  };

  const saveTibero = async () => {
    setBusy(true); setTMsg(''); setTErr(false);
    try {
      const out = await json(await be1Fetch('/api/drivers/tibero', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driver: tDriver, odbcDriver: tOdbc, jdbcJar: tJar }),
      }));
      setTibero(out.tibero);
      setTMsg(t('connection:drv.savedTibero'));
      logUsage('driver.tibero.save', { target: tDriver || 'auto' });
    } catch (e: any) {
      setTMsg(t('connection:drv.saveFailed', { message: e?.message || '' })); setTErr(true);
    } finally { setBusy(false); }
  };

  if (error) return <div className="text-sm text-red-600">{error}</div>;
  if (!oracle || !tibero) return <div className="text-sm text-gray-500">{t('connection:drv.reading')}</div>;

  const a = oracle.active;
  const s = oracle.settings;
  const ts = tibero.settings;

  return (
    <div className="space-y-4 text-sm">
      {/* ───────────── 오라클 ───────────── */}
      <section className="rounded border border-gray-200">
        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
          <div className="font-semibold text-gray-800">Oracle — Instant Client</div>
          <span className={
            'text-xs px-2 py-0.5 rounded-full ' +
            (a.mode === 'thick' ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700')
          }>
            {a.mode === 'thick' ? t('connection:drv.nowThick', { version: a.clientVersion || '' }) : t('connection:drv.nowThin')}
          </span>
        </div>

        <div className="px-3 py-2.5 space-y-2.5">
          {a.mode === 'thick' && a.clientDir && (
            <div className="text-xs text-gray-600 break-all">{t('connection:drv.loadedDir', { dir: a.clientDir })}</div>
          )}
          {oracle.restartRequired && (
            <div className="rounded border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800">
              {t('connection:drv.restartRequired')}
            </div>
          )}

          <div className="text-xs text-gray-500 leading-relaxed">
            {t('connection:drv.oracleIntro')}
            <b> {t('connection:drv.oracleOldAccount')}</b>(<code>password verifier type … not supported</code>),
            <b> {t('connection:drv.oracleOldDb')}</b>, <b>{t('connection:drv.oracleEncryption')}</b>.
          </div>

          <div className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-2 items-start">
            <label className="pt-1.5 text-gray-600">{t('connection:drv.mode')}</label>
            <div>
              <select value={oMode} onChange={(e) => setOMode(e.target.value)} className={inputCls}
                disabled={s.mode.source === 'config'}>
                <option value="auto">{t('connection:drv.modeAuto')}</option>
                <option value="thick">{t('connection:drv.modeThick')}</option>
                <option value="thin">{t('connection:drv.modeThin')}</option>
                <option value="system">{t('connection:drv.modeSystem')}</option>
              </select>
              {lockedNote(t, s.mode)}
            </div>

            <label className="pt-1.5 text-gray-600">{t('connection:drv.clientDir')}</label>
            <div>
              <div className="flex gap-2">
                <input value={oDir} onChange={(e) => { setODir(e.target.value); setProbe(null); }}
                  placeholder={t('connection:drv.clientDirPlaceholder', { root: oracle.bundledRoot })}
                  className={inputCls} disabled={s.clientDir.source === 'config'} />
                <button type="button" onClick={checkOracle} disabled={busy || !oDir.trim()} className={btnCls + ' mt-0.5'}>
                  {t('connection:drv.check')}
                </button>
              </div>
              {lockedNote(t, s.clientDir)}
              <div className="text-[0.6875rem] text-gray-500 mt-0.5">
                <code>oci.dll</code> {t('connection:drv.clientDirHint')} <b>{t('connection:drv.clientDirAscii')}</b>{t('connection:drv.clientDirAsciiTail')}
              </div>
              {probe && (
                <div className={'mt-1 text-xs ' + (probe.loaded ? 'text-green-700' : 'text-red-600')}>
                  {probe.loaded
                    ? t('connection:drv.probeOk', { version: probe.clientVersion || '' })
                    : `✗ ${probe.error}`}
                </div>
              )}
            </div>

            <label className="pt-1.5 text-gray-600">{t('connection:drv.tnsDir')} <span className="text-gray-400">{t('connection:drv.optional')}</span></label>
            <div>
              <input value={oCfg} onChange={(e) => setOCfg(e.target.value)}
                placeholder={t('connection:drv.tnsPlaceholder')}
                className={inputCls} disabled={s.configDir.source === 'config'} />
              {lockedNote(t, s.configDir)}
            </div>
          </div>

          {(oracle.candidates.length > 0 || a.attempts.length > 0) && (
            <details className="text-xs">
              <summary className="cursor-pointer text-gray-600">{t('connection:drv.candidates')}</summary>
              <ul className="mt-1.5 space-y-1">
                {oracle.candidates.map((c) => {
                  const tried = a.attempts.find((x) => x.path === c.path);
                  return (
                    <li key={c.path} className="break-all">
                      <span className="text-gray-400">[{sourceLabel(t, c.origin)}]</span>{' '}
                      {c.path}{' '}
                      {tried
                        ? (tried.ok ? <span className="text-green-700">{t('connection:drv.inUse')}</span>
                          : <span className="text-red-600">{t('connection:drv.failedReason', { reason: tried.reason })}</span>)
                        : (c.problem ? <span className="text-amber-700">— {c.problem}</span>
                          : <span className="text-gray-500">{t('connection:drv.notTried')}</span>)}
                    </li>
                  );
                })}
              </ul>
            </details>
          )}

          <div className="flex items-center justify-between gap-3 pt-1">
            <span className={'text-xs ' + (oErr ? 'text-red-600' : 'text-gray-600')}>{oMsg}</span>
            <button type="button" onClick={saveOracle} disabled={busy} className={primaryCls}>{t('connection:drv.save')}</button>
          </div>
        </div>
      </section>

      {/* ───────────── 티베로 ───────────── */}
      <section className="rounded border border-gray-200">
        <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
          <div className="font-semibold text-gray-800">Tibero</div>
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">
            {t('connection:drv.tiberoNow', { driver: tibero.effectiveDriver === 'jdbc' ? 'JDBC' : 'ODBC' })}
          </span>
        </div>

        <div className="px-3 py-2.5 space-y-2.5">
          <div className="text-xs text-gray-500 leading-relaxed">
            {t('connection:drv.tiberoIntro1')} <b>{t('connection:drv.tiberoOdbc')}</b> {t('connection:drv.tiberoOr')} <b>{t('connection:drv.tiberoJdbc')}</b> {t('connection:drv.tiberoIntro2')}
            {' '}{t('connection:drv.bridges', { odbc: tibero.bridges.pyodbc ? '✓' : '✗', jdbc: tibero.bridges.jaydebeapi ? '✓' : '✗', java: tibero.javaFound ? '✓' : '✗' })}
          </div>

          <div className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-2 items-start">
            <label className="pt-1.5 text-gray-600">{t('connection:drv.method')}</label>
            <div>
              <select value={tDriver} onChange={(e) => setTDriver(e.target.value)} className={inputCls}
                disabled={ts.driver.source === 'config'}>
                <option value="">{t('connection:drv.methodAuto')}</option>
                <option value="odbc">ODBC</option>
                <option value="jdbc">JDBC</option>
              </select>
              {lockedNote(t, ts.driver)}
            </div>

            <label className="pt-1.5 text-gray-600">{t('connection:drv.odbcName')}</label>
            <div>
              <input value={tOdbc} onChange={(e) => setTOdbc(e.target.value)} list="of-tibero-odbc-installed"
                placeholder={t('connection:drv.odbcPlaceholder', { value: ts.odbcDriver.value, source: sourceLabel(t, ts.odbcDriver.source) })}
                className={inputCls} disabled={ts.odbcDriver.source === 'config'} />
              <datalist id="of-tibero-odbc-installed">
                {tibero.odbcInstalled.map((n) => <option key={n} value={n} />)}
              </datalist>
              {lockedNote(t, ts.odbcDriver)}
              <div className="text-[0.6875rem] mt-0.5">
                {tibero.odbcInstalled.length > 0
                  ? <span className="text-green-700">{t('connection:drv.odbcInstalled', { names: tibero.odbcInstalled.join(', ') })}</span>
                  : <span className="text-gray-500">{t('connection:drv.odbcNone')}</span>}
              </div>
              {tibero.odbcInstalled32Only.length > 0 && (
                <div className="text-[0.6875rem] text-amber-700 mt-0.5">
                  {t('connection:drv.odbc32Only', { names: tibero.odbcInstalled32Only.join(', ') })}
                </div>
              )}
            </div>

            <label className="pt-1.5 text-gray-600">{t('connection:drv.jdbcJar')}</label>
            <div>
              <input value={tJar} onChange={(e) => setTJar(e.target.value)}
                placeholder={tibero.bundledJars[0] ? t('connection:drv.jarPlaceholderBundled', { path: tibero.bundledJars[0] }) : t('connection:drv.jarPlaceholder')}
                className={inputCls} disabled={ts.jdbcJar.source === 'config'} />
              {lockedNote(t, ts.jdbcJar)}
              <div className="text-[0.6875rem] mt-0.5">
                {ts.jdbcJar.value
                  ? (tibero.jdbcJarExists
                    ? <span className="text-green-700">✓ {ts.jdbcJar.value} ({sourceLabel(t, ts.jdbcJar.source)})</span>
                    : <span className="text-red-600">{t('connection:drv.jarMissing', { path: ts.jdbcJar.value })}</span>)
                  : <span className="text-gray-500">{t('connection:drv.jarHint')}</span>}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 pt-1">
            <span className={'text-xs ' + (tErr ? 'text-red-600' : 'text-gray-600')}>{tMsg}</span>
            <button type="button" onClick={saveTibero} disabled={busy} className={primaryCls}>{t('connection:drv.save')}</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DriverSettingsPanel;
